<template>
    <div
        v-if="selected || editing"
        class="flex flex-col justify-between gap-2 p-2"
        :class="[
            selected && editing ? 'border-primary-400' : '',
            editing ? 'cursor-pointer border hover:border-primary-600' : ''
        ]"
        @click="$emit('selectCard')"
    >
        <div class="flex gap-3">
            <div>
                <template v-if="paymentMethod.displayInfo.isCard">
                    <img
                        v-if="paymentMethod.displayInfo.brand === 'ALIPAY'"
                        src="~/assets/images/cards/alipay.svg"
                        alt="Alipay"
                        class="w-20"
                    />
                    <img
                        v-else-if="
                            paymentMethod.displayInfo.brand === 'AMEX' ||
                            paymentMethod.displayInfo.brand ===
                                'AMERICAN_EXPRESS'
                        "
                        src="~/assets/images/cards/amex.svg"
                        alt="Amex"
                        class="w-12"
                    />
                    <img
                        v-else-if="paymentMethod.displayInfo.brand === 'DINERS'"
                        src="~/assets/images/cards/diners.svg"
                        alt="Diners"
                        class="w-12"
                    />
                    <img
                        v-else-if="
                            paymentMethod.displayInfo.brand === 'DISCOVER' ||
                            paymentMethod.displayInfo.brand === 'DISC'
                        "
                        src="~/assets/images/cards/discover.svg"
                        alt="Discover"
                        class="w-12"
                    />
                    <img
                        v-else-if="paymentMethod.displayInfo.brand === 'ELO'"
                        src="~/assets/images/cards/elo.svg"
                        alt="ELO"
                        class="w-12"
                    />
                    <img
                        v-else-if="paymentMethod.displayInfo.brand === 'HIPER'"
                        src="~/assets/images/cards/hiper.svg"
                        alt="Hiper"
                        class="w-12"
                    />
                    <img
                        v-else-if="
                            paymentMethod.displayInfo.brand === 'HIPERCARD'
                        "
                        src="~/assets/images/cards/hipercard.svg"
                        alt="HiperCard"
                        class="w-12"
                    />
                    <img
                        v-else-if="paymentMethod.displayInfo.brand === 'JCB'"
                        src="~/assets/images/cards/jcb.svg"
                        alt="JCB"
                        class="w-12"
                    />
                    <img
                        v-else-if="
                            paymentMethod.displayInfo.brand === 'MAESTRO'
                        "
                        src="~/assets/images/cards/maestro.svg"
                        alt="Maestro"
                        class="w-12"
                    />
                    <img
                        v-else-if="
                            paymentMethod.displayInfo.brand === 'MASTERCARD' ||
                            paymentMethod.displayInfo.brand === 'MC'
                        "
                        src="~/assets/images/cards/mastercard.svg"
                        alt="MasterCard"
                        class="w-12"
                    />
                    <img
                        v-else-if="paymentMethod.displayInfo.brand === 'MIR'"
                        src="~/assets/images/cards/mir.svg"
                        alt="Mir"
                        class="w-12"
                    />
                    <img
                        v-else-if="paymentMethod.displayInfo.brand === 'PAYPAL'"
                        src="~/assets/images/cards/paypal.svg"
                        alt="Paypal"
                        class="w-12"
                    />
                    <img
                        v-else-if="
                            paymentMethod.displayInfo.brand === 'UNIONPAY'
                        "
                        src="~/assets/images/cards/unionpay.svg"
                        alt="UnionPay"
                        class="w-12"
                    />
                    <img
                        v-else-if="paymentMethod.displayInfo.brand === 'VISA'"
                        src="~/assets/images/cards/visa.svg"
                        alt="Visa"
                        class="w-12"
                    />
                    <credit-card-icon
                        v-else
                        class="mx-2 h-6 w-6"
                    />
                </template>
                <template v-else>
                    <building-library-icon class="h-6 w-6" />
                </template>
            </div>

            <div class="whitespace-nowrap">
                <label class="font-semibold"
                    >{{ brandFormatted }}&nbsp;&nbsp;<span
                        class="rp-numbers tracking-wider"
                        >&bull;&bull;&bull;&bull;{{
                            paymentMethod.displayInfo.lastFour ||
                            '&bull;&bull;&bull;&bull;'
                        }}</span
                    ></label
                >
                <div v-if="expiresFormatted !== null">
                    <span class="text-sm">Expires {{ expiresFormatted }}</span>
                </div>
                <div v-if="isNewPaymentMethod">
                    <span
                        class="rounded-lg bg-gray-200 px-2 py-1 text-xs font-semibold"
                    >
                        Just Added
                    </span>
                </div>
                <div v-else>
                    <span class="text-sm"
                        >Last used {{ lastUsedFormatted }}</span
                    >
                </div>
                <div>
                    <span
                        v-if="isDefault"
                        class="text-primary rounded-lg bg-primary-300 px-1.5 py-1 text-xs font-semibold uppercase tracking-wider"
                        >Default</span
                    >
                </div>
            </div>
        </div>

        <div
            v-if="editing"
            class="flex justify-center"
        >
            <prime-button
                v-if="selected"
                severity="secondary"
                text
                class="!py-0 px-1"
                label="Selected"
                @click="$emit('selectCard')"
            />

            <prime-button
                v-else
                text
                class="!py-0 px-1"
                :label="
                    paymentMethod.displayInfo.isCard
                        ? 'Use This Card'
                        : 'Use This Payment Method'
                "
                @click="$emit('selectCard')"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { parseTimeless } from '~/composables/support/localization'
import { formatRelative } from 'date-fns'
import { BuildingLibraryIcon, CreditCardIcon } from '@heroicons/vue/24/outline'
import type { PaymentMethodProperties } from '~/components/Checkout/Sections/Payment/payment-method'
import PrimeButton from 'primevue/button'

const props = defineProps<{
    paymentMethod: PaymentMethodProperties
    selectedToken: string | null
    editing: boolean
}>()

defineEmits<{
    (e: 'selectCard'): void
}>()

const { t } = useI18n({ useScope: 'global' })

const expiresFormatted = computed(() => {
    const expirationDate = props.paymentMethod.displayInfo.accountExpirationDate
    if (expirationDate === null) {
        return null
    }

    const expirationDateParsed = parseTimeless(expirationDate)

    // TODO: Localize
    return (
        expirationDateParsed.getMonth() +
        1 +
        '/' +
        String(expirationDateParsed.getFullYear()).substring(2)
    )
})

const lastUsedFormatted = computed(() => {
    const lastUsedDate = props.paymentMethod.displayInfo.lastUsedDate

    if (lastUsedDate === null) {
        return 'N/A'
    }

    const lastUsedDateParsed = new Date(lastUsedDate)

    // TODO: Localize
    return formatRelative(lastUsedDateParsed, new Date())
})

const brandFormatted = computed(() => {
    return props.paymentMethod.displayInfo.brand
})

const isNewPaymentMethod = computed(() => {
    return props.paymentMethod.data.type !== 'StoredPaymentMethod'
})

const isDefault = computed(() => {
    return (
        props.paymentMethod.data.type === 'StoredPaymentMethod' &&
        props.paymentMethod.data.isCustomerDefault
    )
})

const selected = computed(() => {
    return props.selectedToken === props.paymentMethod.key
})
</script>
